import { GetServerSideProps, NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import { observer } from 'mobx-react'
import { useEffect, useState } from 'react'
import { VerticalNavDefaultPageTemplate } from '@/components/templates/VerticalNavDefaultPageTemplate'
import { IUserBase, Language } from '@/types'
import { getStaticStores, getUserAvatar, useStores } from '@/utils'
import { MyHead } from '@/components/molecules/MyHead'
import { ProfileSingleColumnContentTemplate } from '@/components/templates/ProfileSingleColumnContentTemplate'
import { UserDetailHeader } from '@/components/organisms/UserDetailHeader'
import { PrimaryHeading } from '@/components/atoms/PrimaryHeading'
import { FollowerItemList } from '@/components/organisms/FollowerItemList'
import { Button } from '@/components/atoms/Button'
import styles from './index.module.scss'

type Props = {
  user: IUserBase
  staticFollowers: IUserBase[]
  staticHasNextPage: boolean
}

const NUM_OF_ITEMS = 20

const FollowersOfUser: NextPage<Props> = observer(({ user, staticFollowers, staticHasNextPage }) => {
  const { t } = useTranslation(['p'])
  const { users, viewer } = useStores()
  const userInstance = users.createUserInstance(user)
  const title = t('のフォロワー一覧', { name: user?.name })
  const description = t('のフォロワー一覧です。AQA はスタートアップ・ベンチャー企業の求人サイトです。', {
    name: user.name,
  })

  const [isFetching, setIsFetching] = useState(false)
  // ISRで取得した配列より大きい場合、ページ送りの初回リクエストが実行されていることにする
  const hasDoneLoadMore = users.followers[user.username]?.length > staticFollowers.length

  const fetchFollowers = async () => {
    if (isFetching) {
      return
    }
    setIsFetching(true)
    await users.fetchFollowers(user.username, hasDoneLoadMore ? NUM_OF_ITEMS : NUM_OF_ITEMS * 2, !hasDoneLoadMore)
    setIsFetching(false)
  }

  // ページ訪問時最初に実行するメソッド
  const fetchFollowersFirst = async () => {
    await users.fetchFollowers(user.username, NUM_OF_ITEMS, true)
  }

  useEffect(() => {
    users.updateFollowers(user?.username, staticFollowers)
    users.updateHasNextFollowersPage(user?.username, staticHasNextPage)
  }, [user])

  useEffect(() => {
    if (!user || !userInstance) {
      return
    }

    if (viewer.isInitialized && viewer.isSignedIn) {
      userInstance.fetch()
      // isFollowing メソッド等のログインユーザー固有の値を取得するために再度 api を叩く
      fetchFollowersFirst()
    }
  }, [viewer.isInitialized, user])

  return (
    <VerticalNavDefaultPageTemplate>
      <MyHead title={title} description={description} image={getUserAvatar(user, true)} />
      <ProfileSingleColumnContentTemplate header={<UserDetailHeader user={userInstance} />} hasHeaderImage>
        <div className={styles.heading}>
          <PrimaryHeading backgroundText='FOLLOWERS'>{title}</PrimaryHeading>
        </div>
        <div className={styles.content}>
          <FollowerItemList items={users.followers[user.username]} />
        </div>
        {users.hasNextFollowersPage[user.username] && (
          <div className={styles.button}>
            <Button
              styleType='default'
              componentType='button'
              buttonSize='large'
              isLoading={isFetching}
              onClick={() => fetchFollowers()}
            >
              {t('+ Load More')}
            </Button>
          </div>
        )}
      </ProfileSingleColumnContentTemplate>
    </VerticalNavDefaultPageTemplate>
  )
})

export const getServerSideProps: GetServerSideProps = async ({ locale, params }) => {
  const { users, viewer } = getStaticStores()
  await viewer.changeLanguage(locale as Language)
  const username = params.id as string
  const [user, staticFollowers] = await Promise.all([
    users.fetchUser({ username, limitOfReference: 100 }),
    users.fetchFollowers(username, NUM_OF_ITEMS, true),
  ])

  if (!user) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      user,
      staticFollowers,
      staticHasNextPage: users.hasNextFollowersPage[username],
      ...(await serverSideTranslations(locale, ['global', 'companies-users-[id]', 'users-[id]', 'p'])),
    },
  }
}

export default FollowersOfUser
